import axios from "axios"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useEffect, useState } from "react"
import { Button, Modal } from "react-bootstrap"
import type {
	I_VehicleLease,
	I_VehicleLeaseCalculationResponse,
} from "src/common_interfaces/I_VehicleLease"
import Loader from "src/components/common/Loader"
import AxiosConfig from "src/utilities/AxiosConfig"
import LeaseInformationView from "../../LeaseInformationView/LeaseInformationView"
import type { I_ApprovalLeaseAction, I_ApprovalLeaseRequest } from "../types"
import "./RequestDetailModal.css"

dayjs.extend(utc)

const formatUtcToLocal = (iso: string | null | undefined) =>
	iso ? dayjs.utc(iso).local().format("MM/DD/YYYY") : "N/A"

const formatLocalDate = (iso: string | null | undefined) =>
	iso ? dayjs(iso).format("MM/DD/YYYY") : "N/A"

type EditPreview = {
	Current: I_VehicleLease
	Proposed: I_VehicleLease
	CurrentCalculations: I_VehicleLeaseCalculationResponse | null
	ProposedCalculations: I_VehicleLeaseCalculationResponse
}

type LeaseDetails = {
	Current: I_VehicleLease
	CurrentCalculations: I_VehicleLeaseCalculationResponse | null
}

type Props = {
	selectedRequest: I_ApprovalLeaseRequest | null
	selectedAction: I_ApprovalLeaseAction | null
	showCurrentLeaseOnly: boolean
	onClose: () => void
	onAction: (actionName: "Approve" | "Reject") => void
}

export default function RequestDetailModal({
	selectedRequest,
	selectedAction,
	showCurrentLeaseOnly,
	onClose,
	onAction,
}: Props) {
	const requestType =
		selectedRequest?.RequestType ?? selectedAction?.RequestType
	const approvalInstanceId =
		selectedRequest?.ApprovalInstanceId ?? selectedAction?.ApprovalInstanceId
	const vehicleLeaseId = selectedRequest?.RecordId ?? selectedAction?.RecordId

	const [editPreview, setEditPreview] = useState<EditPreview | null>(null)
	const [leaseDetails, setLeaseDetails] = useState<LeaseDetails | null>(null)

	useEffect(() => {
		if (showCurrentLeaseOnly || requestType !== "Edit" || !approvalInstanceId) {
			setEditPreview(null)
			return
		}
		axios
			.get<EditPreview>(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/edit-request/${approvalInstanceId}/preview`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => setEditPreview(res.data))
			.catch((err) => AxiosConfig.handleError(err))
	}, [requestType, approvalInstanceId, showCurrentLeaseOnly])

	useEffect(() => {
		if (!showCurrentLeaseOnly || !vehicleLeaseId) {
			setLeaseDetails(null)
			return
		}
		axios
			.get<LeaseDetails>(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/${vehicleLeaseId}/details`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => setLeaseDetails(res.data))
			.catch((err) => AxiosConfig.handleError(err))
	}, [showCurrentLeaseOnly, vehicleLeaseId])

	if (!selectedRequest && !selectedAction) return null

	const isActionCompleted = selectedAction?.Status !== "Pending"
	const isRequestCompleted = selectedRequest?.Status !== "Pending"
	const isCompletedView =
		(selectedAction && isActionCompleted) ||
		(selectedRequest && isRequestCompleted)

	const dialogClass =
		showCurrentLeaseOnly || requestType === "Edit"
			? isCompletedView
				? "request-detail-modal full-screen-modal"
				: "request-detail-modal full-screen-modal lease-preview-modal"
			: "request-detail-modal"

	return (
		<Modal show onHide={onClose} dialogClassName={dialogClass}>
			<Modal.Header closeButton>
				<Modal.Title>
					{showCurrentLeaseOnly ? (
						"CURRENT LEASE DETAILS"
					) : (
						<>
							{isCompletedView && "COMPLETED "}
							{requestType === "Terminate" && "LEASE TERMINATION REQUEST"}
							{requestType === "Edit" && "EDIT LEASE REQUEST"}
						</>
					)}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{/* View lease mode */}
				{showCurrentLeaseOnly && !leaseDetails && (
					<Loader type="infinite" title="Loading lease details..." />
				)}
				{showCurrentLeaseOnly && leaseDetails && (
					<LeaseInformationView
						lease={leaseDetails.Current}
						calculations={leaseDetails.CurrentCalculations}
					/>
				)}

				{/* Edit type: render diff view */}
				{!showCurrentLeaseOnly && requestType === "Edit" && !editPreview && (
					<Loader type="infinite" title="Loading proposed changes..." />
				)}
				{!showCurrentLeaseOnly && requestType === "Edit" && editPreview && (
					<LeaseInformationView
						lease={editPreview.Proposed}
						calculations={editPreview.ProposedCalculations}
						compareTo={{
							lease: editPreview.Current,
							calculations: editPreview.CurrentCalculations,
						}}
					/>
				)}

				{/* Terminate type: existing inline summaries */}
				{!showCurrentLeaseOnly && requestType === "Terminate" && (
					<>
						{/* Admin pending */}
						{selectedAction && !isActionCompleted && (
							<div className="AdminModalContent">
								<h4>Request awaiting action:</h4>
								<ul className="RequestDetailsList">
									<li>
										<strong>Requested By:</strong>{" "}
										{selectedAction.RequestedByName}
									</li>
									<li>
										<strong>Requested Date:</strong>{" "}
										{formatUtcToLocal(selectedAction.CreatedDateUTC)}
									</li>
									<li>
										<strong>Lease Unit Number:</strong>{" "}
										{selectedAction.UnitNumber}
									</li>
									<li>
										<strong>Proposed Termination Date:</strong>{" "}
										<span style={{ color: "#d9534f", fontWeight: "bold" }}>
											{formatLocalDate(selectedAction.TerminationDate)}
										</span>
									</li>
								</ul>
							</div>
						)}

						{/* Admin completed */}
						{selectedAction && isActionCompleted && (
							<div className="AdminModalContent">
								<h4>Request details</h4>
								<ul className="RequestDetailsList">
									<li>
										<strong>Lease Unit Number:</strong>{" "}
										{selectedAction.UnitNumber}
									</li>
									<li>
										<strong>Termination date:</strong>{" "}
										{formatLocalDate(selectedAction.TerminationDate)}
									</li>
									<li>
										<strong>Requested By:</strong>{" "}
										{selectedAction.RequestedByName}
									</li>
									<li>
										<strong>Date Requested:</strong>{" "}
										{formatUtcToLocal(selectedAction.CreatedDateUTC)}
									</li>
								</ul>
								<h4>Action details</h4>
								<ul className="RequestDetailsList">
									<li>
										<strong>Action Taken:</strong> {selectedAction.Status}
									</li>
									<li>
										<strong>Date Actioned:</strong>{" "}
										{formatUtcToLocal(selectedAction.ActionedDateUTC)}
									</li>
								</ul>
							</div>
						)}

						{/* User pending */}
						{selectedRequest && !isRequestCompleted && (
							<div className="UserModalContent">
								<h4>Pending request:</h4>
								<ul className="RequestDetailsList">
									<li>
										<strong>Date Created:</strong>{" "}
										{formatUtcToLocal(selectedRequest.CreatedDateUTC)}
									</li>
									<li>
										<strong>Lease Unit Number:</strong>{" "}
										{selectedRequest.UnitNumber}
									</li>
									<li>
										<strong>Proposed Termination Date:</strong>{" "}
										<span style={{ color: "#d9534f", fontWeight: "bold" }}>
											{formatLocalDate(selectedRequest.TerminationDate)}
										</span>
									</li>
								</ul>
							</div>
						)}

						{/* User completed */}
						{selectedRequest && isRequestCompleted && (
							<div className="UserModalContent">
								<h4>Request details</h4>
								<ul className="RequestDetailsList">
									<li>
										<strong>Lease Unit Number:</strong>{" "}
										{selectedRequest.UnitNumber}
									</li>
									<li>
										<strong>Termination Date:</strong>{" "}
										{formatLocalDate(selectedRequest.TerminationDate)}
									</li>
									<li>
										<strong>Date Requested:</strong>{" "}
										{formatUtcToLocal(selectedRequest.CreatedDateUTC)}
									</li>
								</ul>
								<h4>Action details</h4>
								<ul className="RequestDetailsList">
									<li>
										<strong>Actioned By:</strong>{" "}
										{selectedRequest.ActionedByName}
									</li>
									<li>
										<strong>Action Taken:</strong> {selectedRequest.Status}
									</li>
									<li>
										<strong>Date Actioned:</strong>{" "}
										{formatUtcToLocal(selectedRequest.ActionedDateUTC)}
									</li>
								</ul>
							</div>
						)}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{selectedAction && !isActionCompleted && (
					<>
						<Button variant="danger" onClick={() => onAction("Reject")}>
							Reject change
						</Button>
						<Button variant="success" onClick={() => onAction("Approve")}>
							Accept change
						</Button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	)
}
